'use client';

import { RouteFailureScreen } from '@/components/system/RouteFailureScreen';
import { useRouteFailure } from '@/components/system/useRouteFailure';
import { getRunningVersion, reloadToVersion } from '@/lib/frontend-version';

type ErrorProps = {
  error: Error & { digest?: string };
  reset: () => void;
};

function copyFor(kind: string, fallback: string): { title: string; message: string } {
  if (kind === 'update') {
    return {
      title: 'A new version was released',
      message: 'A new version was released. Reload to fetch the latest files.',
    };
  }
  if (kind === 'stale-chunk') {
    return {
      title: 'This page could not load its scripts',
      message: 'The scripts for this page failed to load. Reload to try again, or go back.',
    };
  }
  if (kind === 'checking') {
    return {
      title: 'Opening this page',
      message: 'Checking whether a new version is available.',
    };
  }
  return {
    title: 'This page could not open',
    message: fallback,
  };
}

export default function AppError({ error, reset }: ErrorProps) {
  const failure = useRouteFailure(error, 'App-level error');
  const copy = copyFor(failure.kind, error.message || 'An unexpected error occurred. Reload to try again, or go back.');

  return (
    <RouteFailureScreen
      chunkError={failure.kind === 'update' || failure.kind === 'stale-chunk'}
      title={copy.title}
      message={copy.message}
      onReload={() => {
        reloadToVersion(failure.deployed || getRunningVersion());
      }}
      onBack={() => {
        if (window.history.length > 1) {
          window.history.back();
          return;
        }
        reset();
        window.location.assign('/dashboard');
      }}
    />
  );
}
